import React, { useEffect, useState } from 'react';
import { 
  Lightbulb, Heart, Star, Share2, ChevronRight, Sparkles, 
  RefreshCw, BookmarkPlus, Bookmark, Filter, X, Bell, BellOff, Settings, CloudOff
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from '../hooks/use-toast';
import BottomNav from '../components/BottomNav';
import api from '../services/api';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { useOffline } from '../context/OfflineContext';

const CATEGORIES = [
  { id: "all", name: "Toutes", icon: "✨", color: "#9C27B0" },
  { id: "regles", name: "Règles", icon: "🌸", color: "#E91E63" },
  { id: "hygiene", name: "Hygiène", icon: "🧴", color: "#9C27B0" },
  { id: "contraception", name: "Contraception", icon: "💊", color: "#3F51B5" },
  { id: "alimentation", name: "Alimentation", icon: "🍎", color: "#4CAF50" },
  { id: "emotions", name: "Émotions", icon: "😌", color: "#FF9800" },
  { id: "sport", name: "Sport", icon: "🏃‍♀️", color: "#00BCD4" },
];

const TIP_TYPES = {
  conseil: { label: "Conseil", color: "bg-blue-500/20 text-blue-300", icon: "💡" },
  saviez_vous: { label: "Le saviez-vous?", color: "bg-purple-500/20 text-purple-300", icon: "🤔" },
  mythe: { label: "Mythe vs Réalité", color: "bg-red-500/20 text-red-300", icon: "❌" },
  astuce_naturelle: { label: "Astuce naturelle", color: "bg-green-500/20 text-green-300", icon: "🌿" }
};

const TipsAndTricks = () => {
  const { isOnline, cacheData, getCachedData } = useOffline();
  const [tips, setTips] = useState([]);
  const [dailyTip, setDailyTip] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedTip, setSelectedTip] = useState(null);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [dailyTipsEnabled, setDailyTipsEnabled] = useState(true);
  const [isStaleData, setIsStaleData] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  const fetchData = async () => {
    setLoading(true);
    const cacheKey = `/api/tips/${activeCategory}`;

    try {
      const [tipsRes, dailyRes, favoritesRes, settingsRes] = await Promise.all([
        api.get('/tips', { params: { category: activeCategory === 'all' ? null : activeCategory, limit: 100 } }),
        api.get('/tips/daily').catch(() => null),
        api.get('/tips/favorites').catch(() => ({ data: { favorites: [] } })),
        api.get('/tips/settings').catch(() => ({ data: { dailyTipsEnabled: true } }))
      ]);
      
      setTips(tipsRes.data.tips || []);
      if (dailyRes) setDailyTip(dailyRes.data.tip);
      setFavorites(favoritesRes.data.favorites || []);
      setDailyTipsEnabled(settingsRes.data.dailyTipsEnabled ?? true);
      setIsStaleData(false);
      // Cache tips data
      cacheData(cacheKey, {
        tips: tipsRes.data.tips || [],
        dailyTip: dailyRes?.data?.tip || null,
        favorites: favoritesRes.data.favorites || []
      });
    } catch (error) {
      console.error('Error fetching tips:', error);
      // Try cached data
      const cached = await getCachedData(cacheKey);
      if (cached) {
        setTips(cached.data.tips || []);
        if (cached.data.dailyTip) setDailyTip(cached.data.dailyTip);
        setFavorites(cached.data.favorites || []);
        setIsStaleData(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleDailyTips = async () => {
    try {
      const newValue = !dailyTipsEnabled;
      await api.put('/tips/settings', { dailyTipsEnabled: newValue });
      setDailyTipsEnabled(newValue);
      toast({ 
        title: newValue ? 'Notifications activées' : 'Notifications désactivées',
        description: newValue 
          ? 'Vous recevrez une astuce chaque jour à 9h' 
          : 'Vous ne recevrez plus d\'astuces quotidiennes'
      });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const toggleFavorite = async (tipId) => {
    try {
      const res = await api.post(`/tips/favorite/${tipId}`);
      toast({ title: res.data.message });
      
      // Update local state
      if (res.data.favorited) {
        const tip = tips.find(t => t.id === tipId);
        if (tip) setFavorites([...favorites, { ...tip, favorited: true }]);
      } else {
        setFavorites(favorites.filter(f => f.id !== tipId));
      }
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const isFavorited = (tipId) => {
    return favorites.some(f => f.id === tipId);
  };

  const shareTip = async (tip) => {
    const shareText = `💡 ${tip.title}\n\n${tip.content}\n\n- Source: ${tip.source}\n\nDécouvre plus d'astuces sur Fleur de Vie! 🌸`;
    const shareUrl = 'https://fleurde-vie.com';
    const isNative = Capacitor.isNativePlatform();
    
    try {
      if (isNative) {
        // Use Capacitor Share plugin for native
        await Share.share({ 
          title: tip.title, 
          text: shareText,
          url: shareUrl,
          dialogTitle: 'Partager cette astuce'
        });
      } else if (navigator.share) {
        await navigator.share({ title: tip.title, text: shareText, url: shareUrl });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareText + '\n\n' + shareUrl);
        toast({ title: 'Copié!', description: 'L\'astuce a été copiée dans le presse-papier' });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.log('Share failed:', err);
        // Fallback: copy to clipboard
        try {
          await navigator.clipboard.writeText(shareText + '\n\n' + shareUrl);
          toast({ title: 'Copié!', description: 'L\'astuce a été copiée dans le presse-papier' });
        } catch (clipErr) {
          console.error('Clipboard failed:', clipErr);
        }
      }
    }
  };

  const getCategoryColor = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat?.color || '#9C27B0';
  };

  const getCategoryIcon = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat?.icon || '✨';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement des astuces...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Offline Banner */}
      {isStaleData && (
        <div className="bg-orange-600/90 px-4 py-2 flex items-center justify-center gap-2 text-white text-sm" data-testid="tips-offline-banner">
          <CloudOff className="w-4 h-4" />
          <span>Données en cache{!isOnline ? ' (hors ligne)' : ''}</span>
        </div>
      )}
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
              <Lightbulb className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Trucs & Astuces</h1>
              <p className="text-pink-200 text-xs">Conseils d'experts santé</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(true)}
              className="text-white"
            >
              {dailyTipsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5 text-gray-400" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFavorites(!showFavorites)}
              className={`${showFavorites ? 'bg-pink-500/30' : ''} text-white`}
            >
              {showFavorites ? <Bookmark className="w-5 h-5 fill-current" /> : <BookmarkPlus className="w-5 h-5" />}
              <span className="ml-1 text-xs">{favorites.length}</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-4 space-y-4">
        {/* Daily Tip */}
        {dailyTip && !showFavorites && (
          <Card className="p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-300 font-semibold text-sm">Astuce du jour</span>
            </div>
            <h3 className="text-white font-bold mb-2">{dailyTip.title}</h3>
            <p className="text-white/80 text-sm line-clamp-3">{dailyTip.content}</p>
            <div className="flex items-center justify-between mt-3">
              <span className="text-yellow-300/60 text-xs">{dailyTip.source}</span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => shareTip(dailyTip)}
                  className="text-yellow-300 hover:bg-yellow-500/20"
                >
                  <Share2 className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedTip(dailyTip)}
                  className="text-yellow-300 hover:bg-yellow-500/20"
                >
                  Lire plus
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Category Filter */}
        {!showFavorites && (
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {CATEGORIES.map(cat => (
              <Button
                key={cat.id}
                size="sm"
                variant={activeCategory === cat.id ? "default" : "outline"}
                onClick={() => setActiveCategory(cat.id)}
                className={`whitespace-nowrap flex-shrink-0 ${
                  activeCategory === cat.id 
                    ? 'bg-pink-500 text-white' 
                    : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                }`}
              >
                <span className="mr-1">{cat.icon}</span>
                {cat.name}
              </Button>
            ))}
          </div>
        )}

        {/* Favorites View */}
        {showFavorites && (
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-400 fill-pink-400" />
              Mes favoris ({favorites.length})
            </h2>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowFavorites(false)}
              className="text-gray-400"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Tips List */}
        <div className="space-y-3">
          {(showFavorites ? favorites : tips).map((tip, index) => {
            const typeInfo = TIP_TYPES[tip.type] || TIP_TYPES.conseil;
            
            return (
              <Card 
                key={tip.id || index}
                className="p-4 bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/15 transition-all cursor-pointer"
                onClick={() => setSelectedTip(tip)}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                    style={{ backgroundColor: `${getCategoryColor(tip.category)}30` }}
                  >
                    {getCategoryIcon(tip.category)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${typeInfo.color}`}>
                        {typeInfo.icon} {typeInfo.label}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold text-sm mb-1 line-clamp-1">
                      {tip.title}
                    </h3>
                    <p className="text-pink-200/80 text-xs line-clamp-2">
                      {tip.content}
                    </p>
                  </div>
                  
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(tip.id);
                    }}
                    className={`flex-shrink-0 ${isFavorited(tip.id) ? 'text-pink-400' : 'text-gray-400'}`}
                  >
                    <Heart className={`w-5 h-5 ${isFavorited(tip.id) ? 'fill-current' : ''}`} />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Empty State */}
        {showFavorites && favorites.length === 0 && (
          <div className="text-center py-12">
            <Heart className="w-16 h-16 text-pink-300/30 mx-auto mb-4" />
            <p className="text-pink-200">Aucun favori pour l'instant</p>
            <p className="text-pink-300/60 text-sm mt-1">
              Appuyez sur le coeur pour sauvegarder vos astuces préférées
            </p>
            <Button
              onClick={() => setShowFavorites(false)}
              className="mt-4 bg-pink-500 hover:bg-pink-600"
            >
              Découvrir les astuces
            </Button>
          </div>
        )}

        {/* Stats */}
        {!showFavorites && (
          <div className="flex items-center justify-center gap-6 py-4 text-center">
            <div>
              <p className="text-2xl font-bold text-white">{tips.length}</p>
              <p className="text-pink-200 text-xs">Astuces</p>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div>
              <p className="text-2xl font-bold text-white">6</p>
              <p className="text-pink-200 text-xs">Catégories</p>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div>
              <p className="text-2xl font-bold text-white">{favorites.length}</p>
              <p className="text-pink-200 text-xs">Favoris</p>
            </div>
          </div>
        )}
      </div>

      {/* Tip Detail Dialog */}
      <Dialog open={!!selectedTip} onOpenChange={() => setSelectedTip(null)}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-md max-h-[80vh] overflow-y-auto">
          {selectedTip && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                    style={{ backgroundColor: `${getCategoryColor(selectedTip.category)}30` }}
                  >
                    {getCategoryIcon(selectedTip.category)}
                  </div>
                  <div>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${TIP_TYPES[selectedTip.type]?.color || TIP_TYPES.conseil.color}`}>
                      {TIP_TYPES[selectedTip.type]?.label || 'Conseil'}
                    </span>
                  </div>
                </div>
                <DialogTitle className="text-white text-lg">
                  {selectedTip.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="py-4">
                <p className="text-pink-100 text-sm leading-relaxed whitespace-pre-line">
                  {selectedTip.content}
                </p>
                
                <div className="mt-4 pt-4 border-t border-purple-700">
                  <p className="text-pink-300/60 text-xs">
                    Source: {selectedTip.source}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => toggleFavorite(selectedTip.id)}
                  variant="outline"
                  className={`flex-1 border-pink-500 ${isFavorited(selectedTip.id) ? 'bg-pink-500/20 text-pink-300' : 'text-pink-300'}`}
                >
                  <Heart className={`w-4 h-4 mr-2 ${isFavorited(selectedTip.id) ? 'fill-current' : ''}`} />
                  {isFavorited(selectedTip.id) ? 'Favori' : 'Ajouter aux favoris'}
                </Button>
                <Button
                  onClick={() => shareTip(selectedTip)}
                  className="flex-1 bg-pink-500 hover:bg-pink-600"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Partager
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Paramètres des notifications
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Astuce du jour</p>
                  <p className="text-pink-200 text-xs">Recevez une astuce santé chaque matin à 9h</p>
                </div>
              </div>
              <Switch
                checked={dailyTipsEnabled}
                onCheckedChange={toggleDailyTips}
              />
            </div>

            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-blue-300 text-sm">
                {dailyTipsEnabled 
                  ? '✅ Vous recevrez une notification chaque jour à 9h avec une astuce personnalisée.'
                  : '❌ Les notifications quotidiennes sont désactivées. Vous pouvez toujours consulter les astuces ici.'}
              </p>
            </div>
          </div>
          
          <Button
            onClick={() => setShowSettings(false)}
            className="w-full bg-pink-500 hover:bg-pink-600"
          >
            Fermer
          </Button>
        </DialogContent>
      </Dialog>

      <BottomNav active="tips" />
    </div>
  );
};

export default TipsAndTricks;
